import React, { useContext, useLayoutEffect, useState } from "react";
import { Paper, Button, DialogTitle, DialogActions } from "@mui/material";
import moment from "moment";
import { gql } from "@apollo/client";

import { FieldsContainer, AppForm } from "controls";
import { showMessage, DESKTOP_BUILD, sleep } from "helpers";
import { BooleanInput } from "inputs";
import { useAction, useProgressBar } from "hooks";
import { authContext } from "contexts";
import logoImage from "images/logo_button.png";
import DialogFooter from "views/viewport/DialogFooter";

export default function Login() {
  const { authenticate } = useContext(authContext);

  const loginAction = useAction(gql`
    mutation ($input: LoginInput!) {
      login(input: $input) {
        token
        requireEmailOtp
      }
    }
  `);
  const requestLoginToken = useAction(gql`
    mutation ($input: RequestLoginTokenInput!) {
      requestLoginToken(input: $input) {
        staleAt
      }
    }
  `);

  const progressBar = useProgressBar();
  const [login, loginSet] = useState("");
  const [staleAt, staleAtSet] = useState(null);
  const [mode, modeSet] = useState("login");
  const [requireEmailOtp, requireEmailOtpSet] = useState(false);

  const [title, fields, submitLabel, onSubmit] = {
    login: [
      "登录优建云",
      [
        {
          name: "login",
          required: true,
          label: "电子邮箱",
          value: login,
          options: {
            autoComplete: "username",
          },
        },
        {
          name: "password",
          required: true,
          label: "密码",
          value: login,
          options: {
            type: "password",
            autoComplete: "password",
          },
          helperText: (
            <>
              尚未设置密码或者忘记密码?{" "}
              <a href="#" onClick={() => modeSet("requestLoginToken")}>
                重置密码
              </a>
            </>
          ),
        },
        requireEmailOtp && {
          name: "emailOtpCode",
          required: true,
          label: "验证码",
          value: "",
          options: {
            autoComplete: "off",
          },
          helperText: (
            <>
              验证码已发送至您的电子邮箱，请输入验证码以完成登录，
              <a
                href="#"
                onClick={async (event) => {
                  progressBar(() => sleep(500)); // additional progress bar to wai for onSubmit event kicking in
                  const form = event.currentTarget.closest("form");
                  requireEmailOtpSet(false);
                  await sleep(100);
                  form.querySelector("button[type=submit]").click();
                }}
              >
                重新发送验证码
              </a>
              。
            </>
          ),
        },
        !DESKTOP_BUILD && {
          name: "rememberMe",
          label: "在这台设备上记住我",
          value: false,
          inputComponent: BooleanInput,
        },
      ].filter(Boolean),
      "登录",
      async (formData) => {
        const { login, password, rememberMe, emailOtpCode } = formData;
        const result = await loginAction({
          input: {
            login,
            password,
            rememberMe,
            emailOtpCode,
          },
        });
        const { token, requireEmailOtp } = result.login;
        requireEmailOtpSet(requireEmailOtp);
        if (requireEmailOtp) {
          showMessage({
            type: "warning",
            message: "请检查您的电子邮箱，输入验证码",
          });
          return;
        }
        await authenticate({ token });

        showMessage({ message: "登录成功" });
      },
    ],
    requestLoginToken: [
      "重置密码",
      [
        {
          name: "login",
          required: true,
          label: "电子邮箱",
          value: login,
          options: {
            autoComplete: "username",
          },
        },
      ],
      "下一步",
      async (formData) => {
        const { login } = formData;
        const result = await requestLoginToken({
          input: {
            login,
          },
        });
        loginSet(formData.login);
        staleAtSet(result.requestLoginToken.staleAt);
        modeSet("useLoginToken");
      },
    ],
    useLoginToken: [
      "重置密码",
      [
        {
          label: "电子邮箱",
          name: "login",
          value: login,
          options: { readOnly: true },
        },
        {
          name: "loginTokenCode",
          required: true,
          label: "验证码",
          options: {
            autoComplete: "off",
          },
          helperText: (
            <LoginTokenHelperText
              staleAt={staleAt}
              onBackClick={() => modeSet("requestLoginToken")}
            />
          ),
        },
      ],
      "下一步",
      async (formData) => {
        const { loginTokenCode } = formData;
        const result = await loginAction({
          input: {
            login,
            loginTokenCode,
          },
        });
        const token = result.login.token;
        window._requireResetPassword = true;
        await authenticate({ token });

        showMessage({ message: "登录成功" });
      },
    ],
  }[mode];

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexFlow: "row nowrap",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper
        component={AppForm}
        onSubmit={onSubmit}
        elevation={DESKTOP_BUILD ? 0 : undefined}
        style={{
          flex: "0 1 auto",
          padding: 20,
          margin: 10,
          display: "flex",
          flexFlow: "column nowrap",
          alignItems: "center",
          width: 480,
        }}
      >
        <img src={logoImage} alt="优建云" style={{ width: 64, height: 64 }} />
        <DialogTitle>{title}</DialogTitle>
        <FieldsContainer fields={fields} style={{ width: "100%" }} />
        <DialogActions style={{ width: "100%" }}>
          <Button type="submit" variant="contained" fullWidth>
            {submitLabel}
          </Button>
        </DialogActions>
        {mode !== "login" && (
          <DialogActions style={{ width: "100%" }}>
            <Button fullWidth onClick={() => modeSet("login")}>
              返回登录
            </Button>
          </DialogActions>
        )}
        <DialogFooter />
      </Paper>
    </div>
  );
}

function LoginTokenHelperText({ staleAt, onBackClick }) {
  const [seconds, secondsSet] = useState(60);

  useLayoutEffect(() => {
    const refreshDisplay = () => {
      const seconds = moment(staleAt).diff(moment(), "seconds");
      secondsSet(seconds);
    };
    const timer = setInterval(refreshDisplay, 1000);
    refreshDisplay();
    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <>
      验证码已发送至您的电子邮箱，
      {seconds > 0 ? (
        <>
          如果未收到可以在{seconds}
          秒后重新发送。
          <a href="#" onClick={onBackClick}>
            更改电子邮箱
          </a>
        </>
      ) : (
        <a href="#" onClick={onBackClick}>
          重新获取
        </a>
      )}
    </>
  );
}
